import React, { useState, useEffect } from 'react'
import { createFormBag, FormBag, FormData, FormErrors, validateFormBag } from 'react-formage'

import { connect } from 'containers/store'

import { FieldSection } from 'components/ui/fieldset'
import { Input } from 'components/ui/input'

import { MediaField } from 'domains/media/components/media-field'

import { Media } from 'generated/mos/media'
import { VisitorInfoPage, Rectangle } from 'generated/mos/org/getty/visitorinfo'
import { UpdateVisitorInfoPageRequest } from 'generated/mos/org/getty/visitorinfomanagement'

import { pick, areObjectsEqual, areRefValuesEqual } from 'helpers/core'
import { ENVIRONMENT, appEnvironment } from 'domains/org/getty/utils/environment'
import { baseDeepLinkDomain, gettyCenterSiteId, gettyVillaSiteId } from 'helpers/deep-links'
import { convertEntityToFormValues } from 'helpers/schema'
import { Status } from 'helpers/status'

import { actionCreators, GettyActionCreators } from '../../../../actions'

import { GettyStore } from '../../../../store'

import { imagePageSchema } from '../../../../schemas/visitorInfo'

type FormValues = {
  headerTitle: string | undefined;
  altText:  string | undefined;
  listTitle: string | undefined;
  top: string;
  right: string;
  bottom: string;
  left: string;
}

type ActionProps =
  Pick<GettyActionCreators, 'gettyUpdateVisitorInfoPageRequest'>

type ConnectedProps = Pick<GettyStore['getty'], 'visitorInfoUpdate'>

type DirectProps = {
  readonly page: VisitorInfoPage.Entity | undefined;
  readonly setSubmit: (submit: () => void) => void;
  readonly setHasSubmitted: (submitted: boolean) => void;
  readonly setModified: (modifed: boolean | undefined) => void;
  readonly pageId: string;
}

type Props = ActionProps & ConnectedProps & DirectProps


const getProperties = (pageProperties: VisitorInfoPage.SiteMap.Entity) => {
  const { defaultMapZoomFrame, ...properties} = pageProperties
  return {
    ...properties,
    ...defaultMapZoomFrame
  }
}

const ImagePageUpdateView = (props: Props) => {
  const {
    visitorInfoUpdate,
    gettyUpdateVisitorInfoPageRequest,
    page,
    setSubmit,
    setHasSubmitted,
    setModified,
    pageId,
  } = props

  // set up form values
  const [ formBag, setFormBag ] = useState<FormBag<FormValues> | undefined>(undefined)
  const [ imageMedia, setImageMedia ] = useState<Media.Ref | undefined>(undefined)
  const [ iconMedia, setIconMedia ] = useState<Media.Ref | undefined>(undefined)

  const pageProperties = page && page.pageProperties as VisitorInfoPage.SiteMap.Entity
  const pageValues: Partial<VisitorInfoPage.SiteMap.Entity & VisitorInfoPage.Entity & Rectangle.Entity> = pick({
    ...VisitorInfoPage.SiteMap.defaults,
    ...VisitorInfoPage.defaults,
    ...Rectangle.defaults,
    ...page,
    ...pageProperties ? getProperties(pageProperties): null,
  }, 'headerTitle', 'altText', 'listTitle', 'top', 'bottom', 'left', 'right')

  // setup the initial form values.
  const initialFormValues: FormValues | undefined = page ?
    { ...convertEntityToFormValues<VisitorInfoPage.SiteMap.Entity & VisitorInfoPage.Entity | VisitorInfoPage.SiteMap.Entity>(pageValues, imagePageSchema) as any as FormValues,
      top: String(pageValues.top),
      right: String(pageValues.right),
      bottom: String(pageValues.bottom),
      left: String(pageValues.left),
    }
    : undefined

  const initialImageValue = page && pageProperties && imagePageSchema.mapPdfRef.read(pageProperties.mapPdfRef) as any as Media.Ref | undefined
  const initialIconValue = page && imagePageSchema.listIconRef.read(page.listIconRef) as any as Media.Ref | undefined

  if (page && initialFormValues && !formBag && visitorInfoUpdate.status === Status.Ready) {
    setFormBag(createFormBag(initialFormValues))
    setImageMedia(initialImageValue)
    setIconMedia(initialIconValue)
  }

  useEffect(() => {
    setSubmit(() => () => onSubmit())
  })

  // handle the form ui.
  const modified = initialFormValues && formBag && (
    !areObjectsEqual(initialFormValues, formBag.values) ||
    !areRefValuesEqual(initialImageValue, imageMedia) ||
    !areRefValuesEqual(initialIconValue, iconMedia)
  )

  setModified(modified)

  const validateForm = (values: FormValues) => {
    const errors: FormErrors<FormValues> = {};

    if (!values.listTitle || !values.listTitle.trim().length) {
      errors.listTitle = 'List title is required'
    }

    if(isNaN(Number(values.top))) errors.top = 'Top must be a number'
    if(isNaN(Number(values.right))) errors.right = 'Right must be a number'
    if(isNaN(Number(values.bottom))) errors.bottom = 'Bottom must be a number'
    if(isNaN(Number(values.left))) errors.left = 'Left must be a number'

    return errors
  }

  const onSubmit = () => {
    return formBag && page && submit(formBag, page)
  }

  const submit = (formBag: FormBag<FormValues>, page: VisitorInfoPage.Entity) => {
    const bag = validateFormBag(formBag, validateForm)
    setFormBag(bag)

    if(!bag.valid) {
      return false
    }

    const pageProperties = page && page.pageProperties as VisitorInfoPage.SiteMap.Entity
    const { listTitle, headerTitle, altText, top, left, bottom, right } = formBag.values

    setHasSubmitted(true)

    gettyUpdateVisitorInfoPageRequest({
      ...UpdateVisitorInfoPageRequest.defaults,
      visitorInfoPageRef: VisitorInfoPage.mustRef(page.ref),
      updateMask: {
        type: 'google.protobuf.FieldMask',
        fields: [
          'listIconRef',
          'listTitle',
          'siteMap',
        ]
      },
      listIconRef: imagePageSchema.listIconRef.write(iconMedia) || iconMedia,
      listTitle: imagePageSchema.listTitle.write(listTitle, page.listTitle) || listTitle,
      pageProperties: {
        ...VisitorInfoPage.SiteMap.defaults,
        headerTitle: imagePageSchema.headerTitle.write(headerTitle, pageProperties.headerTitle) || headerTitle,
        altText: imagePageSchema.altText.write(altText, pageProperties.altText) || altText,
        mapPdfRef: imagePageSchema.mapPdfRef.write(imageMedia) || imageMedia,
        defaultMapZoomFrame: {
          ...Rectangle.defaults,
          top: Number(top),
          right: Number(right),
          bottom: Number(bottom),
          left: Number(left),
        }
      }
    })
    return false
  }

  const stagingCenterMapPageId = '0173039f-3d0e-ec97-72e9-0235845d77a8'
  const prodCenterMapPageId = '01730372-6096-13b8-9138-a5a3ea618f45'

  const stagingVillaMapPageId = '017303ab-8e41-6d40-b044-af92f096078a'
  const prodVillaMapPageId = '017303ab-8e41-6d40-b044-af92f096078a'

  const siteId = (() => {
    if (ENVIRONMENT.STAGING === appEnvironment || appEnvironment == "local") {
      if (pageId === stagingCenterMapPageId) {
          return gettyCenterSiteId;
      } else if (pageId === stagingVillaMapPageId) {
          return gettyVillaSiteId;
      }
    } else if (ENVIRONMENT.PROD === appEnvironment) {
      if (pageId === prodCenterMapPageId) {
        return gettyCenterSiteId;
      } else if (pageId === prodVillaMapPageId) {
        return gettyVillaSiteId;
      }
    }
  })()

  const baseDeepLinkDomain = (() => {
      switch (appEnvironment) {
        case ENVIRONMENT.PROD:
          return 'https://links.getty.artpro.io'
        case ENVIRONMENT.STAGING:
          return 'https://staging.links.getty.artpro.io'
        default:
          return 'https://links.getty.artpro.io'
      }
    })()

  const deepLinkUrl = `${baseDeepLinkDomain}?deeplink=site/${siteId}/map`;

  return formBag && page && (
    <form noValidate onSubmit={(event) => { event.preventDefault() }}>
      <FormData
        bag={formBag}
        onUpdate={(event) => {
          setFormBag(event.bag)
          setSubmit(() => () => onSubmit())
          setModified(modified)
        }}
        validate={() => ({})}
      >
        <Input
          label="Title"
          field="headerTitle"
        />
        <FieldSection />
        <MediaField
          kind="MEDIA_KIND_PDF"
          mediaRef={imageMedia}
          onUpdate={(entity) => {
            setImageMedia(entity ? entity.ref : undefined)
            setSubmit(() => () => onSubmit())
          }}
          label="Map Image"
        />
        <Input
          label="Zoom frame top"
          description={"The default zoom \"frame\" that the map will animate to on presentation"}
          field="top"
          error={formBag.touched.top ? formBag.errors.top : undefined}
          isAssociated={true}
        />
        <Input
          label="Zoom frame right"
          description={"The default zoom \"frame\" that the map will animate to on presentation"}
          field="right"
          error={formBag.touched.right ? formBag.errors.right : undefined}
          isAssociated={true}
        />
        <Input
          label="Zoom frame bottom"
          description={"The default zoom \"frame\" that the map will animate to on presentation"}
          field="bottom"
          error={formBag.touched.bottom ? formBag.errors.bottom : undefined}
          isAssociated={true}
        />
        <Input
          label="Zoom frame left"
          description={"The default zoom \"frame\" that the map will animate to on presentation"}
          field="left"
          error={formBag.touched.left ? formBag.errors.left : undefined}
          isAssociated={true}
        />
        <Input
          label="Alternate Text"
          description="Description of what the image contains if it cannot be shown."
          field="altText"
          isAssociated={true}
        />
        <FieldSection />
        <Input
          label="List title"
          field="listTitle"
          error={formBag.touched.listTitle ? formBag.errors.listTitle : undefined}
          description="The list title is the page's name in the navigation menu."
        />
        <MediaField
          kind="MEDIA_KIND_RASTER_IMAGE"
          mediaRef={iconMedia}
          onUpdate={(entity) => {
            setIconMedia(entity ? entity.ref : undefined)
            setSubmit(() => () => onSubmit())
          }}
          label="List icon"
        />
      </FormData>
      <>
        <h4>Deep Link</h4>
        <div style={{ display: 'flex', justifyContent: 'space-between', flexDirection: 'column'}}>
          <div><a href={deepLinkUrl} target="_blank" rel="noopener noreferrer">{deepLinkUrl}</a></div>
        </div>
      </>
    </form>
  )
}

export const ImagePageUpdate = connect<ConnectedProps, ActionProps, DirectProps, GettyStore, GettyActionCreators>(
  store => pick(store.getty, 'visitorInfoUpdate'),
  {
    ...pick(actionCreators, 'gettyUpdateVisitorInfoPageRequest'),
  },
)(ImagePageUpdateView)
